import {
  Address,
  ClinicOrder,
  ClinicOrderQuery,
  ClinicOrderStatus,
  CreateOrderRequest,
  GuestContact,
  ListResult,
  OrderItem,
  OrderTimelineEvent,
  PayResult,
  ResendReceiptResult,
} from '../types';
import { delay } from './delay';
import {
  db,
  findActiveBusinessByUserId,
  findBuyerAccountByUserId,
  findProductById,
  getOrCreateCart,
} from './db';
import {
  ERR_CONFLICT,
  ERR_NOT_FOUND,
  ERR_VALIDATION,
} from './errors';
import { readUserIdFromToken } from './auth-utils';
import { getClinicToken } from '../services/http';

const RESERVATION_MINUTES = 15;
const round2 = (n: number): number => Math.round(n * 100) / 100;
const nowIso = () => new Date().toISOString();
const nextOrderId = () => `o-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
const nextPaymentId = () => `pay-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const orderUserMap = new Map<string, string>();
const guestOrderEmailMap = new Map<string, string>();

const setOrderOwner = (orderId: string, userId: string) => orderUserMap.set(orderId, userId);
const setGuestOwner = (orderId: string, email: string) =>
  guestOrderEmailMap.set(orderId, email.trim().toLowerCase());
export const getOrderOwner = (orderId: string): string | undefined => orderUserMap.get(orderId);

const userOwns = (orderId: string, userId: string): boolean =>
  getOrderOwner(orderId) === userId;

const guestOwns = (orderId: string, email: string): boolean =>
  guestOrderEmailMap.get(orderId) === email.trim().toLowerCase();

const isAuthenticated = (): boolean => !!getClinicToken();

const guestEmailReceiptCooldown = new Map<string, number>();

const recordPayment = (
  orderId: string,
  amount: number,
  status: 'PAID' | 'FAILED',
): void => {
  db.payments.unshift({
    id: nextPaymentId(),
    orderId,
    amount,
    status,
    method: 'CARD',
    createdAt: nowIso(),
    paidAt: status === 'PAID' ? nowIso() : undefined,
  });
};

const restoreReservedStock = (order: ClinicOrder) => {
  for (const item of order.items) {
    const product = findProductById(item.productId);
    if (!product) continue;
    product.availableStock += item.quantity;
    product.stockStatus =
      product.availableStock === 0
        ? 'OUT_OF_STOCK'
        : product.availableStock <= 20
          ? 'LOW_STOCK'
          : 'IN_STOCK';
  }
};

const expireIfDue = (order: ClinicOrder) => {
  if (order.status !== 'PENDING_PAYMENT') return;
  if (!order.paymentDeadline) return;
  if (Date.parse(order.paymentDeadline) > Date.now()) return;
  order.status = 'EXPIRED';
  order.timeline.push({ status: 'EXPIRED', occurredAt: nowIso() });
  restoreReservedStock(order);
};

const sweep = () => {
  for (const order of db.orders) expireIfDue(order);
};

const isAddressComplete = (a?: Address): a is Address =>
  !!a && !!a.line1 && !!a.suburb && !!a.state && !!a.postcode && !!a.country;

const buildItemsFromCart = (
  userId: string,
): { items: OrderItem[]; subtotal: number; gstTotal: number; total: number } => {
  const record = getOrCreateCart(userId);
  if (record.items.length === 0) throw ERR_VALIDATION('Cart is empty');
  const items: OrderItem[] = [];
  let subtotal = 0;
  let gstTotal = 0;
  for (const line of record.items) {
    const product = findProductById(line.productId);
    if (!product) throw ERR_CONFLICT(`Product ${line.productId} is no longer available`);
    if (product.stockStatus === 'OUT_OF_STOCK' || product.availableStock < line.quantity) {
      throw ERR_CONFLICT(`Insufficient stock for ${product.nameEn}`, {
        productId: product.id,
        availableStock: product.availableStock,
      });
    }
    if (product.moq && product.moq > 1 && line.quantity < product.moq) {
      throw ERR_VALIDATION(`Minimum order quantity for ${product.nameEn} is ${product.moq}`, {
        productId: product.id,
        moq: product.moq,
      });
    }
    const lineTotal = round2(product.salePrice * line.quantity);
    const gst = round2(lineTotal * product.gstRate);
    subtotal += lineTotal;
    gstTotal += gst;
    items.push({
      productId: product.id,
      productNameEn: product.nameEn,
      unit: product.unit,
      quantity: line.quantity,
      unitPrice: product.salePrice,
      gstRate: product.gstRate,
      lineTotal,
    });
  }
  subtotal = round2(subtotal);
  gstTotal = round2(gstTotal);
  return { items, subtotal, gstTotal, total: round2(subtotal + gstTotal) };
};

const reserveStockForItems = (items: OrderItem[]) => {
  for (const item of items) {
    const product = findProductById(item.productId);
    if (!product) continue;
    product.availableStock -= item.quantity;
    if (product.availableStock <= 0) product.stockStatus = 'OUT_OF_STOCK';
    else if (product.availableStock <= 20) product.stockStatus = 'LOW_STOCK';
  }
};

const ensureOrder = (orderId: string, userId: string): ClinicOrder => {
  const order = db.orders.find((o) => o.id === orderId);
  if (!order || !userOwns(orderId, userId)) throw ERR_NOT_FOUND('Order');
  expireIfDue(order);
  return order;
};

const ensureGuestOrder = (orderId: string, email: string): ClinicOrder => {
  const order = db.orders.find((o) => o.id === orderId);
  if (!order || !guestOwns(orderId, email)) throw ERR_NOT_FOUND('Order');
  expireIfDue(order);
  return order;
};

const matchesQuery = (order: ClinicOrder, query: ClinicOrderQuery): boolean => {
  if (query.status && order.status !== query.status) return false;
  if (query.keyword) {
    const keyword = query.keyword.trim().toLowerCase();
    if (keyword.length > 0 && !order.id.toLowerCase().includes(keyword)) return false;
  }
  return true;
};

export const orderMock = {
  create: async (req: CreateOrderRequest): Promise<ClinicOrder> => {
    await delay();
    if (req.shippingAddress && !isAddressComplete(req.shippingAddress)) {
      throw ERR_VALIDATION('Shipping address is incomplete');
    }

    if (isAuthenticated()) {
      const userId = readUserIdFromToken();
      const account = findBuyerAccountByUserId(userId);
      if (!account) throw ERR_NOT_FOUND('Buyer account');
      if (account.status !== 'ACTIVE')
        throw ERR_VALIDATION('Buyer account is not active');
      const found = findActiveBusinessByUserId(userId);
      if (found && found.business.status !== 'ACTIVE')
        throw ERR_VALIDATION('Business is not active');
      const totals = buildItemsFromCart(userId);
      reserveStockForItems(totals.items);
      const orderId = nextOrderId();
      const created: ClinicOrder = {
        id: orderId,
        status: 'PENDING_PAYMENT',
        createdAt: nowIso(),
        paymentDeadline: new Date(Date.now() + RESERVATION_MINUTES * 60 * 1000).toISOString(),
        items: totals.items,
        subtotal: totals.subtotal,
        gstTotal: totals.gstTotal,
        freight: 0,
        handlingFee: 0,
        total: totals.total,
        shippingAddress: req.shippingAddress ? { ...req.shippingAddress } : null,
        deliveryEmail: req.deliveryEmail,
        timeline: [{ status: 'PENDING_PAYMENT', occurredAt: nowIso() }],
        customerType: 'REGISTERED',
      };
      db.orders.unshift(created);
      setOrderOwner(orderId, userId);
      const cartRecord = getOrCreateCart(userId);
      cartRecord.items = [];
      return { ...created };
    }

    // Guest order path.
    if (!req.guestContact?.email) throw ERR_VALIDATION('Email is required');
    if (!req.guestContact.firstName || !req.guestContact.lastName) {
      throw ERR_VALIDATION('Contact name is required');
    }
    const lines = req.guestItems ?? [];
    if (lines.length === 0) throw ERR_VALIDATION('Cart is empty');
    const items: OrderItem[] = [];
    let subtotal = 0;
    let gstTotal = 0;
    for (const line of lines) {
      const product = findProductById(line.productId);
      if (!product) {
        throw ERR_CONFLICT(`Product ${line.productId} is no longer available`);
      }
      if (
        product.stockStatus === 'OUT_OF_STOCK' ||
        product.availableStock < line.quantity
      ) {
        throw ERR_CONFLICT(`Insufficient stock for ${product.nameEn}`, {
          productId: product.id,
          availableStock: product.availableStock,
        });
      }
      if (product.moq && product.moq > 1 && line.quantity < product.moq) {
        throw ERR_VALIDATION(`Minimum order quantity for ${product.nameEn} is ${product.moq}`);
      }
      const lineTotal = round2(product.salePrice * line.quantity);
      const gst = round2(lineTotal * product.gstRate);
      subtotal += lineTotal;
      gstTotal += gst;
      items.push({
        productId: product.id,
        productNameEn: product.nameEn,
        unit: product.unit,
        quantity: line.quantity,
        unitPrice: product.salePrice,
        gstRate: product.gstRate,
        lineTotal,
      });
    }
    subtotal = round2(subtotal);
    gstTotal = round2(gstTotal);
    reserveStockForItems(items);
    const orderId = nextOrderId();
    const created: ClinicOrder = {
      id: orderId,
      status: 'PENDING_PAYMENT',
      createdAt: nowIso(),
      paymentDeadline: new Date(Date.now() + RESERVATION_MINUTES * 60 * 1000).toISOString(),
      items,
      subtotal,
      gstTotal,
      freight: 0,
      handlingFee: 0,
      total: round2(subtotal + gstTotal),
      shippingAddress: req.shippingAddress ? { ...req.shippingAddress } : null,
      deliveryEmail: req.deliveryEmail,
      timeline: [{ status: 'PENDING_PAYMENT', occurredAt: nowIso() }],
      customerType: 'GUEST',
      guestEmail: req.guestContact.email.trim(),
      guestContact: { ...req.guestContact, email: req.guestContact.email.trim() },
    };
    db.orders.unshift(created);
    setGuestOwner(orderId, created.guestEmail!);
    return { ...created };
  },

  pay: async (orderId: string, _req?: { sourceId?: string }): Promise<ClinicOrder> => {
    const result: PayResult = 'SUCCESS';
    await delay();
    let order: ClinicOrder;
    if (isAuthenticated()) {
      const userId = readUserIdFromToken();
      order = ensureOrder(orderId, userId);
    } else {
      const stored = db.orders.find((o) => o.id === orderId);
      if (!stored || stored.customerType !== 'GUEST') throw ERR_NOT_FOUND('Order');
      expireIfDue(stored);
      order = stored;
    }
    if (order.status === 'EXPIRED') throw ERR_CONFLICT('Order reservation expired');
    if (order.status !== 'PENDING_PAYMENT') throw ERR_CONFLICT('Order is not awaiting payment');

    if (result === 'SUCCESS') {
      order.status = 'PROCESSING';
      order.timeline.push(
        { status: 'PAID', occurredAt: nowIso() },
        { status: 'PROCESSING', occurredAt: nowIso() },
      );
      recordPayment(order.id, order.total, 'PAID');
    } else {
      order.status = 'PAYMENT_FAILED';
      order.timeline.push({ status: 'PAYMENT_FAILED', occurredAt: nowIso() });
      recordPayment(order.id, order.total, 'FAILED');
      restoreReservedStock(order);
    }
    return { ...order };
  },

  cancel: async (orderId: string): Promise<ClinicOrder> => {
    await delay();
    const userId = readUserIdFromToken();
    const order = ensureOrder(orderId, userId);
    if (order.status !== 'PENDING_PAYMENT') {
      throw ERR_CONFLICT('Only pending-payment orders can be cancelled');
    }
    order.status = 'CANCELLED';
    order.timeline.push({ status: 'CANCELLED', occurredAt: nowIso() });
    restoreReservedStock(order);
    return { ...order };
  },

  simulatePayFailed: async (orderId: string): Promise<ClinicOrder> => {
    // Mock can't simulate Square failure without a fake gateway; just return the
    // order as-is. Real backend handles the `cnon:card-nonce-declined` case.
    return orderMock.pay(orderId);
  },

  payGuest: async (
    publicOrderNo: string,
    _email: string,
    _req?: { sourceId?: string },
  ): Promise<ClinicOrder> => {
    return orderMock.pay(publicOrderNo);
  },

  cancelGuest: async (publicOrderNo: string, _email: string): Promise<ClinicOrder> => {
    return orderMock.cancel(publicOrderNo);
  },

  list: async (
    query: ClinicOrderQuery = {},
  ): Promise<ListResult<ClinicOrder>> => {
    await delay();
    const userId = readUserIdFromToken();
    sweep();
    const owned = db.orders.filter((o) => userOwns(o.id, userId));
    const filtered = owned.filter((o) => matchesQuery(o, query));
    const skip = query.skip ?? 0;
    const take = query.take ?? 0;
    const items = take === 0 ? filtered.slice(skip) : filtered.slice(skip, skip + take);
    return { items: items.map((o) => ({ ...o })), skip, take, total: filtered.length };
  },

  detail: async (orderId: string): Promise<ClinicOrder> => {
    await delay();
    if (isAuthenticated()) {
      const userId = readUserIdFromToken();
      const order = ensureOrder(orderId, userId);
      return { ...order };
    }
    const order = db.orders.find((o) => o.id === orderId);
    if (!order || order.customerType !== 'GUEST') throw ERR_NOT_FOUND('Order');
    expireIfDue(order);
    return { ...order };
  },

  confirmReceived: async (orderId: string): Promise<ClinicOrder> => {
    await delay();
    const userId = readUserIdFromToken();
    const order = ensureOrder(orderId, userId);
    if (order.status !== 'DISPATCHED') {
      throw ERR_CONFLICT('Order is not in dispatched state');
    }
    order.status = 'COMPLETED';
    order.timeline.push(
      { status: 'RECEIVED', occurredAt: nowIso() },
      { status: 'COMPLETED', occurredAt: nowIso() },
    );
    return { ...order };
  },

  reorder: async (orderId: string): Promise<{ addedCount: number; skipped: { productId: string; reason: string }[] }> => {
    await delay();
    const userId = readUserIdFromToken();
    const order = ensureOrder(orderId, userId);
    const cart = getOrCreateCart(userId);
    let added = 0;
    const skipped: { productId: string; reason: string }[] = [];
    for (const item of order.items) {
      const product = findProductById(item.productId);
      if (!product || product.stockStatus === 'OUT_OF_STOCK' || product.availableStock <= 0) {
        skipped.push({ productId: item.productId, reason: 'OUT_OF_STOCK' });
        continue;
      }
      const qty = Math.min(item.quantity, product.availableStock);
      const existing = cart.items.find((c) => c.productId === item.productId);
      if (existing) existing.quantity = qty;
      else cart.items.push({ productId: item.productId, quantity: qty });
      added += 1;
    }
    return { addedCount: added, skipped };
  },

  lookup: async (orderId: string, email: string): Promise<ClinicOrder> => {
    await delay();
    const order = db.orders.find((o) => o.id === orderId);
    if (!order || order.customerType !== 'GUEST') throw ERR_NOT_FOUND('Order');
    if (!guestOwns(orderId, email)) throw ERR_NOT_FOUND('Order');
    expireIfDue(order);
    return { ...order };
  },

  resendReceipt: async (orderId: string): Promise<ResendReceiptResult> => {
    await delay();
    const order = db.orders.find((o) => o.id === orderId);
    if (!order) throw ERR_NOT_FOUND('Order');
    const recipient =
      order.customerType === 'GUEST'
        ? order.guestEmail
        : (() => {
            const userId = orderUserMap.get(orderId);
            if (!userId) return undefined;
            const account = findBuyerAccountByUserId(userId);
            return account?.email;
          })();
    if (!recipient) throw ERR_NOT_FOUND('Recipient email');
    const now = Date.now();
    const lastSent = guestEmailReceiptCooldown.get(orderId) ?? 0;
    if (now - lastSent < 60_000) {
      throw ERR_CONFLICT('Please wait before requesting another receipt email');
    }
    guestEmailReceiptCooldown.set(orderId, now);
    return { sentTo: recipient, cooldownSeconds: 60 };
  },

  downloadInvoicePdf: async (orderId: string): Promise<void> => {
    await delay();
    // Mock fakes the download — produces an empty blob so the click handler
    // doesn't throw, but a real PDF only comes from the live backend endpoint.
    const blob = new Blob([`Mock invoice for ${orderId}`], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${orderId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // Test helper: simulate dispatch (used to validate confirmReceived end-to-end).
  // Real backend will perform this from warehouse portal.
  __forceDispatch: async (orderId: string, carrier = 'AusPost', trackingNumber = 'MOCK1234567'): Promise<ClinicOrder> => {
    const userId = readUserIdFromToken();
    const order = ensureOrder(orderId, userId);
    order.status = 'DISPATCHED';
    order.carrier = carrier;
    order.trackingNumber = trackingNumber;
    const events: OrderTimelineEvent[] = [];
    if (!order.timeline.some((e) => e.status === 'DISPATCHED')) {
      events.push({ status: 'DISPATCHED' as ClinicOrderStatus, occurredAt: nowIso() });
    }
    order.timeline.push(...events);
    return { ...order };
  },
};
